// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  hideTour,
  listUserDetails,
  setTourActivity,
  setActiveTourComponent,
} from "../../store/auth/action";
import { logAmplitudeEvent } from "../../utils/amplitude/functions";
import amplitude from "../../utils/amplitude/initiate";
import { getSession } from "../../utils/sessionstore";

// NEW Figma design for the tour step popup - reuses the exact same skip/finish
// logic the old LeftForkTourCard/TopForkTourCard/RightForkTour components had
// (hideTour + setTourActivity(false) + setActiveTourComponent({})), just with
// the new light-theme card layout instead of the old dark "speech bubble" card.
// `total` defaults to 10 to match the new step count shown in the Figma ("X of
// 10"), even though only a handful of steps are actually wired up so far.
// arrowSide picks which corner the speech-bubble pointer sits on, matching the
// old LeftForkTourCard ("left", used by the sidebar-icon steps) vs
// TopForkTourCard-style ("right") arrow placement; pass "none" to render no
// pointer at all - used by the dashboard-content steps (AEP/What's New/Explore
// More) whose Figma has no arrow, since they sit near a whole content block
// rather than pointing at one small icon.
// `size` switches between the two Figma card sizes: "sm" (default, w-72/p-4,
// used for the sidebar-icon steps) and "lg" (w-96/p-5, used for the
// dashboard-content steps) - same component, just parameterized, per the
// reuse mandate instead of a second near-duplicate card component.
// `arrowTop`, when given by TourAnchor, is the exact px offset (from this
// card's own top edge) that lines the arrow up with the target icon's
// vertical center - needed because the card's top edge no longer matches the
// icon's top edge once TourAnchor clamps the card upward to stay on-screen
// (e.g. the "Help" step, near the bottom of the rail). Falls back to a fixed
// 16px for callers that don't go through TourAnchor (none currently pass an
// arrow at all - they use arrowSide="none").
const TourCard = ({ onNext, number, total = 10, title, description, isLast = false, arrowSide = "left", size = "sm", arrowTop }) => {
  const dispatch = useDispatch();
  const isLarge = size === "lg";
  const { clientID } = useParams();
  const { clientname, userDetails } = useSelector((state) => ({
    clientname: state.clientname,
    userDetails: state.userDetails,
  }));
  // NEW - a genuinely first-time user (see the matching flag/comment in
  // TourStarter.jsx) gets no "Skip Tour" anywhere in the whole flow, not just on
  // the welcome popup - so this hides it here too, on every one of the 10 step
  // cards, not only the last one (which already has no Skip regardless, via
  // `isLast` below - the last step never had a skip option since there's
  // nothing left to skip to).
  const isFirstTimeTour = Boolean(userDetails?.showTour);

  // NEW - the tour previously had zero Amplitude tracking (old and new alike),
  // so there was no way to see how far people actually get through it, where
  // they skip, or whether they finish. Fired centrally here since every step's
  // card is this same component - covers all 10 steps without touching each
  // step's own host file.
  const logTourEvent = (eventName) =>
    logAmplitudeEvent(amplitude, eventName, {
      stepNumber: number,
      stepTitle: title,
      clientName: clientname,
      clientId: clientID ?? getSession("client_id"),
      eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
    });

  // NEW - syncs the local redux copy of userDetails.showTour to false once the
  // walkthrough actually ends (skip or finish), not the moment it starts (see
  // TourStarter.jsx's onTakeToor for why: syncing any earlier would make "Skip
  // Tour" pop back in partway through THIS SAME first-time run, as soon as the
  // backend write resolves). By the time this fires, the backend is already
  // updated too (from TourStarter's callUpdateUser when the tour started), so
  // this just catches redux up to match it - no separate API call needed here.
  function syncShowTourFalse() {
    dispatch(listUserDetails({ ...userDetails, showTour: false }));
  }

  function onSkip() {
    logTourEvent("Tour Skipped");
    dispatch(hideTour());
    dispatch(setTourActivity(false));
    dispatch(setActiveTourComponent({}));
    syncShowTourFalse();
  }

  function onTourFinish() {
    logTourEvent("Tour Completed");
    dispatch(hideTour());
    dispatch(setTourActivity(false));
    dispatch(setActiveTourComponent({}));
    syncShowTourFalse();
  }

  function handleNext() {
    logTourEvent("Tour Step Next Clicked");
    onNext?.();
  }

  return (
    <div
      className={`relative bg-general-background rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] inline-flex flex-col justify-start items-start ${
        isLarge ? "w-96 p-5 gap-6" : "w-72 p-4 gap-4"
      }`}
    >
      {arrowSide !== "none" && (
        // Plain CSS border-triangle instead of an SVG path - the SVG kept showing
        // a faint seam line right where it met the card (tried fixing the path's
        // geometry twice - a curved back edge, then a flat one not quite reaching
        // the SVG's own boundary - the line survived both, pointing to the
        // scale-x-[-1] flip's sub-pixel anti-aliasing at this ~5px size, not the
        // path itself). A CSS triangle has no viewBox/scaling/anti-aliasing seam
        // to leak through - the colored border sits flush against the card by
        // construction, and left vs right just swaps which side gets the color
        // instead of flipping a shape.
        <div
          style={{ top: arrowTop ?? 16 }}
          className={`absolute w-0 h-0 border-y-[6px] border-y-transparent ${
            arrowSide === "left"
              ? "-left-[5px] border-r-[5px] border-r-general-background"
              : "-right-[5px] border-l-[5px] border-l-general-background"
          }`}
        />
      )}
      <div className={`self-stretch flex flex-col justify-start items-start ${isLarge ? "gap-3" : "gap-2"}`}>
        <div
          className={`self-stretch text-left text-black font-medium font-['Geist'] ${
            isLarge ? "text-base leading-6" : "text-sm leading-5"
          }`}
        >
          {title}
        </div>
        <div
          className={`self-stretch text-left text-general-muted-foreground font-normal font-['Geist'] ${
            isLarge ? "text-sm leading-5" : "text-xs leading-4"
          }`}
        >
          {description}
        </div>
      </div>
      <div className={`self-stretch inline-flex justify-start items-center ${isLarge ? "gap-3" : "gap-2"}`}>
        <div
          className={`flex-1 text-left text-general-muted-foreground font-normal font-['Geist'] ${
            isLarge ? "text-sm leading-5" : "text-xs leading-4"
          }`}
        >
          {number} of {total}
        </div>
        {!isLast && !isFirstTimeTour && (
          <button
            type="button"
            onClick={onSkip}
            className={`bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border flex justify-center items-center gap-2 ${
              isLarge ? "min-h-9 px-4 py-2" : "min-h-8 px-3 py-1.5"
            }`}
          >
            <span
              className={`text-center text-general-foreground font-medium font-['Geist'] ${
                isLarge ? "text-sm leading-5" : "text-xs leading-4"
              }`}
            >
              Skip Tour
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={isLast ? onTourFinish : handleNext}
          className={`bg-general-primary rounded-lg flex justify-center items-center gap-2 ${
            isLarge ? "min-h-9 px-4 py-2" : "min-h-8 px-3 py-1.5"
          }`}
        >
          <span
            className={`text-center text-general-primary-foreground font-medium font-['Geist'] ${
              isLarge ? "text-sm leading-5" : "text-xs leading-4"
            }`}
          >
            {isLast ? "Finish" : "Next"}
          </span>
        </button>
      </div>
    </div>
  );
};

export default memo(TourCard);
